// Publish a firmware build — the app's replacement for the C# desktop tool's
// "Upload Bin File" button. Shared by both shells; the transport and the three
// server calls live in core/devices/otaBinFiles.js.
//
// ── Why this asks a question before it uploads ─────────────────────────────
// The upload IS the deployment: the file lands at a fixed URL and every device
// whose row names it fetches the new bytes on its next Update Code. So the
// filename is not a label, it is the target — and the one thing the operator
// must see before pressing the button is WHO ELSE is on that name. Publishing
// `OtaForSwitching.ino.bin` re-points 30-odd device types at once; publishing
// `OtaForSwitchingV2.ino.bin` re-points nothing until it is linked. Those are
// completely different actions and they look identical in a file picker.
//
// Hence the two states after a file is chosen: REPLACES (with the list of types
// affected) or NEW (with a note that nothing will pick it up on its own).
//
// ⚠️ THERE IS NO "UNDO" AND NO VERSIONING ON THE SERVER. The old bytes are
// gone the moment the new ones land — the only way back is to upload the
// previous build again, from a copy that had better still exist locally.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  TextField, Autocomplete, Alert, AlertTitle, LinearProgress, Chip, Link,
  Divider,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MemoryIcon from '@mui/icons-material/Memory';
import { getDeviceTypes } from '../../core/api/api';
import { isNativePlatform } from '../../core/platform';
import {
  publishBinFile, checkBinName, getBinFileRow,
  getLocalFolder, setLocalFolder, joinLocalPath, readEspImage, rememberBuildSha,
  STEP_UPLOAD, STEP_RECORD, STEP_RESOLVE, STEP_LINK, EXPECTED_BIN_DIR,
} from '../../core/devices/otaBinFiles';
import { FIT_SM } from './fitDialog';

const STEP_LABEL = {
  [STEP_UPLOAD]: 'Uploading the file…',
  [STEP_RECORD]: 'Recording the build…',
  [STEP_RESOLVE]: 'Reading the record back…',
  [STEP_LINK]: 'Linking the device type…',
};

// What a failure at each step means for the hardware. The distinction is the
// whole point of reporting the step: after STEP_UPLOAD the firmware is already
// live and only the bookkeeping is missing, which is a very different morning.
const STEP_CONSEQUENCE = {
  [STEP_UPLOAD]: 'Nothing was uploaded. The devices are untouched.',
  [STEP_RECORD]: 'The file IS on the server and devices will fetch it — but the'
    + ' build date was not recorded, so Firmware will keep reporting the old one.',
  [STEP_RESOLVE]: 'The file is on the server and the build was recorded. Only the'
    + ' device-type link was not made.',
  [STEP_LINK]: 'The file is on the server and the build was recorded. Only the'
    + ' device-type link was not made.',
};

const kb = (n) => `${(n / 1024).toFixed(1)} KB`;

export default function UploadBinFileDialog({ open, onClose, onPublished }) {
  const inputRef = useRef(null);

  const [types, setTypes] = useState([]);
  const [typesError, setTypesError] = useState('');
  const [file, setFile] = useState(null);
  const [uartPins, setUartPins] = useState('');
  // The folder is remembered per computer and joined to the picked filename —
  // the same mechanism the Device Types screen uses, so both routes record
  // HexFileLocalLocation in the same shape. See otaBinFiles.js.
  const [folder, setFolder] = useState(getLocalFolder());
  const [localPath, setLocalPath] = useState('');
  const [linkType, setLinkType] = useState(null);
  const [existing, setExisting] = useState(null);   // the OTABinFile row, if any

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [pct, setPct] = useState(null);
  const [error, setError] = useState(null);         // { message, consequence, url }
  const [result, setResult] = useState(null);

  // The type list is both the picker's options and the answer to "who is on
  // this filename" — one call serves both, so it is fetched once per opening.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    getDeviceTypes()
      .then((res) => { if (alive) setTypes(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (alive) setTypesError('Could not load the device types, so the list below is empty. The upload itself still works.'); });
    return () => { alive = false; };
  }, [open]);

  const reset = useCallback(() => {
    setFile(null); setUartPins(''); setLocalPath(''); setLinkType(null);
    setExisting(null); setBusy(false); setStep(''); setPct(null);
    setError(null); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const close = useCallback(() => { if (!busy) { reset(); onClose(); } }, [busy, reset, onClose]);

  const nameCheck = useMemo(() => (file ? checkBinName(file.name) : null), [file]);

  // Device types already pointing at this filename — i.e. everything this
  // upload silently re-flashes. Matched on BinFileName because that is what
  // OTABinFile is keyed on and what the device actually fetches.
  const affected = useMemo(() => {
    if (!file) return [];
    return types.filter((t) => String(t.BinFileName || '').trim() === file.name);
  }, [types, file]);

  const pick = useCallback(async (e) => {
    const f = e.target.files && e.target.files[0];
    setError(null); setResult(null); setExisting(null);
    setFile(f || null);
    if (!f) return;
    // Prefill from the row this will overwrite, so a re-publish keeps the
    // UartPins and the recorded build path instead of blanking them.
    try {
      const row = await getBinFileRow(f.name);
      if (row) {
        setExisting(row);
        setUartPins(String(row.UartPins || ''));
        // The recorded path wins over the remembered folder — it is what the
        // build actually was last time, and the folder is only a guess.
        setLocalPath(String(row.HexFileLocalLocation || '') || joinLocalPath(folder, f.name));
      } else {
        setUartPins('');
        setLocalPath(joinLocalPath(folder, f.name));
      }
    } catch (err) {
      // Non-fatal: it only costs the prefill.
      setExisting(null);
      setLocalPath(joinLocalPath(folder, f.name));
    }
  }, [folder]);

  const run = useCallback(async () => {
    setBusy(true); setError(null); setResult(null); setPct(null);
    try {
      const out = await publishBinFile({
        file,
        uartPins,
        localPath,
        deviceId: linkType ? linkType.DeviceID : null,
        onStep: setStep,
        onProgress: setPct,
      });
      setLocalFolder(folder);
      // Same as the Device Types path: record the build's sha under this name.
      try {
        const img = await readEspImage(file);
        if (img.ok && img.sha8) rememberBuildSha(file.name, img.sha8);
      } catch (e) { /* advisory only */ }
      setResult(out);
      if (onPublished) onPublished(out);
    } catch (err) {
      setError({
        message: err.message || String(err),
        consequence: STEP_CONSEQUENCE[err.step] || '',
        url: err.url || '',
      });
    } finally {
      setBusy(false); setStep(''); setPct(null);
    }
  }, [file, uartPins, localPath, folder, linkType, onPublished]);

  const canRun = !!file && !!nameCheck && nameCheck.ok && !busy;

  return (
    <Dialog open={open} onClose={close} {...FIT_SM}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MemoryIcon fontSize="small" />
        Publish a firmware build
      </DialogTitle>

      <DialogContent dividers>
        {result ? (
          <Alert severity="success">
            <AlertTitle>{`${result.binFileName} is live`}</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <Link href={result.url} target="_blank" rel="noreferrer" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12.5 }}>
                {result.url}
              </Link>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {`Recorded as OTABinFileId ${result.otaBinFileId ?? '—'}, built ${result.stampedAt}.`}
              {result.linkedDeviceId != null
                ? ` Device type ${result.linkedDeviceId} now points at it.`
                : ' No device type was re-linked.'}
            </Typography>
            {/* Publishing does not push. Saying so here stops the "I uploaded
                it, why is nothing updated" round trip. */}
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Devices do not fetch this on their own — they take it on their next
              Update Code. Load the Firmware list to see who is now behind.
            </Typography>
          </Alert>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => inputRef.current && inputRef.current.click()}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {file ? 'Choose a different file' : 'Choose a .bin file'}
            </Button>
            {/* ⚠️ NO `accept` ON NATIVE. Android's document picker turns the
                accept list into MIME types, and a .bin has no registered one —
                it resolves to application/octet-stream, which several pickers
                then show as an EMPTY folder. The operator sees their build
                missing and blames the app. Unfiltered on the phone plus
                checkBinName as the real guard; the filter is a browser-only
                convenience. */}
            <input
              ref={inputRef}
              type="file"
              {...(isNativePlatform() ? {} : { accept: '.bin' })}
              hidden
              onChange={pick}
            />

            {file && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                  {file.name}
                </Typography>
                <Chip size="small" label={kb(file.size)} sx={{ height: 20, fontSize: 11 }} />
              </Box>
            )}

            {nameCheck && !nameCheck.ok && (
              <Alert severity="error" sx={{ mt: 1.5 }}>{nameCheck.error}</Alert>
            )}
            {nameCheck && nameCheck.ok && nameCheck.warning && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>{nameCheck.warning}</Alert>
            )}

            {/* The decisive question: replacing something in use, or adding
                something nothing points at yet. */}
            {file && nameCheck && nameCheck.ok && (
              affected.length > 0 || existing ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  <AlertTitle sx={{ fontSize: 14 }}>
                    {`This replaces the existing ${file.name}`}
                  </AlertTitle>
                  <Typography variant="body2">
                    {affected.length > 0
                      ? `${affected.length} device type${affected.length === 1 ? '' : 's'} point at this file`
                        + ' and will fetch the new build on their next Update Code:'
                      : 'The record exists but no device type points at it.'}
                  </Typography>
                  {affected.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {affected.slice(0, 8).map((t) => `${t.DeviceID} ${t.DeviceName}`).join(' · ')}
                      {affected.length > 8 ? ` · +${affected.length - 8} more` : ''}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    The old bytes are overwritten and are not kept anywhere.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  <AlertTitle sx={{ fontSize: 14 }}>New firmware file</AlertTitle>
                  <Typography variant="body2">
                    {`Nothing points at ${file.name} yet, so no device changes until a`}
                    {' device type is linked to it — below, or later on Device Types.'}
                  </Typography>
                </Alert>
              )
            )}

            {file && nameCheck && nameCheck.ok && (
              <>
                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth size="small" label="UART pins" value={uartPins}
                  onChange={(e) => setUartPins(e.target.value)} disabled={busy}
                  helperText="OTABinFile.UartPins — e.g. D7D8. Blank is normal; most rows have none."
                  sx={{ mb: 2 }}
                />

                {/* A browser gives the file's NAME and nothing else, so unlike
                    the desktop tool the path cannot be read automatically. The
                    folder is typed once and remembered; the joined result stays
                    editable. Left blank it is omitted from the write rather
                    than sent as "", which would erase what the C# recorded. */}
                <TextField
                  fullWidth size="small" label="Local bin folder (this computer)"
                  value={folder}
                  onChange={(e) => {
                    setFolder(e.target.value);
                    if (file) setLocalPath(joinLocalPath(e.target.value, file.name));
                  }}
                  disabled={busy}
                  placeholder="H:/Mix_Data/espressif/claude/OtaForSwitching"
                  helperText="Remembered on this computer and joined to the file name."
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth size="small" label="Local bin file path (saved)"
                  value={localPath} onChange={(e) => setLocalPath(e.target.value)} disabled={busy}
                  helperText="OTABinFile.HexFileLocalLocation — where this build came from. Left blank, the existing value is kept."
                  sx={{ mb: 2 }}
                />

                <Autocomplete
                  options={types}
                  value={linkType}
                  onChange={(e, v) => setLinkType(v)}
                  disabled={busy || !types.length}
                  getOptionLabel={(t) => `${t.DeviceID} — ${t.DeviceName}`}
                  isOptionEqualToValue={(a, b) => a.DeviceID === b.DeviceID}
                  renderOption={(props, t) => (
                    <li {...props} key={t.DeviceID}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2">{`${t.DeviceID} — ${t.DeviceName}`}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {t.BinFileName || 'no firmware file'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      size="small"
                      label="Point a device type at this build (optional)"
                      helperText={linkType
                        ? `Currently on ${linkType.BinFileName || 'no file'} — this changes what every device of that type downloads.`
                        : 'Leave empty to record the build without re-pointing anything.'}
                    />
                  )}
                />

                {typesError && <Alert severity="warning" sx={{ mt: 1.5 }}>{typesError}</Alert>}
              </>
            )}

            {busy && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant={step === STEP_UPLOAD && pct != null ? 'determinate' : 'indeterminate'}
                  value={pct ?? 0}
                />
                <Typography variant="caption" color="text.secondary">
                  {STEP_LABEL[step] || 'Working…'}
                  {step === STEP_UPLOAD && pct != null ? ` ${pct}%` : ''}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Publish failed</AlertTitle>
                <Typography variant="body2">{error.message}</Typography>
                {error.consequence && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {error.consequence}
                  </Typography>
                )}
                {error.url && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {error.url}
                  </Typography>
                )}
              </Alert>
            )}

            {!file && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {`The file is stored under ${EXPECTED_BIN_DIR} on the server, under its own name — so the name you upload is the name devices ask for.`}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} disabled={busy}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            color={affected.length > 0 ? 'warning' : 'primary'}
            disabled={!canRun}
            onClick={run}
            startIcon={<UploadFileIcon />}
            sx={{ textTransform: 'none' }}
          >
            {affected.length > 0 ? `Replace (${affected.length} types)` : 'Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
